import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { ToastProvider } from './components/ToastContext'
import AppShell from './components/AppShell'
import ProtectedRoute from './components/ProtectedRoute'
import SignIn from './pages/SignIn'
import AuthCallback from './pages/AuthCallback'
import AcceptInvite from './pages/AcceptInvite'
import Home from './pages/Home'
import Money from './pages/Money'
import Prints from './pages/Prints'
import Maintenance from './pages/Maintenance'
import NotFound from './pages/NotFound'
import Settings from './pages/Settings'
import { useTheme } from './hooks/useTheme'
import PrintersPage from './pages/PrintersPage'
import PrinterFormPage from './pages/PrinterFormPage'
import Onboarding from './pages/Onboarding'
import SalesList from './pages/money/SalesList'
import SaleForm from './pages/money/SaleForm'
import ExpensesList from './pages/money/ExpensesList'
import ExpenseForm from './pages/money/ExpenseForm'
import PrintsList from './pages/prints/PrintsList'
import PrintForm from './pages/prints/PrintForm'
import FilamentList from './pages/prints/FilamentList'
import FilamentForm from './pages/prints/FilamentForm'
import FilamentEditPage from './pages/prints/FilamentEditPage'
import ProductsList from './pages/prints/ProductsList'
import ProductForm from './pages/prints/ProductForm'
import ProductDetail from './pages/prints/ProductDetail'
import MaintenanceDue from './pages/maintenance/MaintenanceDue'
import MaintenanceHistory from './pages/maintenance/MaintenanceHistory'
import MaintenanceSchedule from './pages/maintenance/MaintenanceSchedule'
import MaintenanceLogForm from './pages/maintenance/MaintenanceLogForm'
import ScheduleForm from './pages/maintenance/ScheduleForm'
import QueueList from './pages/prints/QueueList'
import QueueForm from './pages/prints/QueueForm'
import BuildBulkFillPage from './pages/prints/BuildBulkFillPage'
import Journal from './pages/Journal'
import JournalForm from './pages/JournalForm'
import Reports from './pages/Reports'
import Calendar from './pages/Calendar'
import Export from './pages/Export'
import Calculator from './pages/Calculator'
import Activity from './pages/Activity'
import HardwareLibrary from './pages/HardwareLibrary'
import ConsigneesPage from './pages/settings/ConsigneesPage'
import ConsigneeDetail from './pages/settings/ConsigneeDetail'
import MomModeGuard from './components/MomModeGuard'
import MomShell from './components/MomShell'
import MomHome from './pages/mom/MomHome'
import MomSales from './pages/mom/MomSales'
import MomSettings from './pages/mom/MomSettings'

export default function App() {
  useTheme() // initialize theme on app load
  return (
    <ToastProvider>
    <BrowserRouter>
      <Routes>
        <Route path="/sign-in" element={<SignIn />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />
        {/* Mom Mode: separate layout, separate routes, separate guard. */}
        <Route
          element={
            <ProtectedRoute>
              <MomModeGuard requireRole="consignee">
                <MomShell />
              </MomModeGuard>
            </ProtectedRoute>
          }
        >
          <Route path="/mom" element={<MomHome />} />
          <Route path="/mom/sales" element={<MomSales />} />
          <Route path="/mom/settings" element={<MomSettings />} />
        </Route>

        {/* Owner: existing AppShell, now guarded against accidental mom visits. */}
        <Route
          element={
            <ProtectedRoute>
              <MomModeGuard requireRole="owner">
                <AppShell />
              </MomModeGuard>
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<Home />} />
          <Route path="/money" element={<Money />}>
            <Route index element={<SalesList />} />
            <Route path="expenses" element={<ExpensesList />} />
          </Route>
          <Route path="/prints" element={<Prints />}>
            <Route index element={<PrintsList />} />
            <Route path="filament" element={<FilamentList />} />
            <Route path="products" element={<ProductsList />} />
            <Route path="queue" element={<QueueList />} />
          </Route>
          <Route path="/prints/new" element={<PrintForm />} />
          <Route path="/prints/queue/new" element={<QueueForm />} />
          <Route path="/prints/queue/:id/edit" element={<QueueForm />} />
          <Route path="/prints/filament/new" element={<FilamentForm />} />
          <Route path="/prints/filament/:id" element={<FilamentEditPage />} />
          <Route path="/prints/products/new" element={<ProductForm />} />
          <Route path="/prints/products/:id" element={<ProductDetail />} />
          <Route path="/prints/products/:id/edit" element={<ProductForm />} />
          <Route path="/maintenance" element={<Maintenance />}>
            <Route index element={<MaintenanceDue />} />
            <Route path="history" element={<MaintenanceHistory />} />
            <Route path="schedule" element={<MaintenanceSchedule />} />
          </Route>
          <Route path="/maintenance/log" element={<MaintenanceLogForm />} />
          <Route path="/maintenance/schedule/new" element={<ScheduleForm />} />
          <Route path="/journal" element={<Journal />} />
          <Route path="/journal/new" element={<JournalForm />} />
          <Route path="/journal/:id/edit" element={<JournalForm />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/calendar" element={<Calendar />} />
          <Route path="/settings/export" element={<Export />} />
          <Route path="/calculator" element={<Calculator />} />
          <Route path="/activity" element={<Activity />} />
          <Route path="/settings/hardware" element={<HardwareLibrary />} />
          <Route path="/settings/consignees" element={<ConsigneesPage />} />
          <Route path="/settings/consignees/:id" element={<ConsigneeDetail />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/settings/printers" element={<PrintersPage />} />
          <Route path="/settings/printers/new" element={<PrinterFormPage />} />
          <Route path="/settings/printers/:id" element={<PrinterFormPage />} />
          <Route path="/money/sales/new" element={<SaleForm />} />
          <Route path="/money/sales/:id/edit" element={<SaleForm />} />
          <Route path="/money/expenses/new" element={<ExpenseForm />} />
          <Route path="/money/expenses/:id/edit" element={<ExpenseForm />} />
          <Route path="/prints/:id/edit" element={<PrintForm />} />
          <Route path="/builds/:id/bulk-fill" element={<BuildBulkFillPage />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
    </ToastProvider>
  )
}
